'use client';

import Link from 'next/link';

export default function TermsPage() {
    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            {/* Header */}
            <header className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
                    <Link href="/" className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
                            <span className="text-white font-bold text-sm">W</span>
                        </div>
                        <span className="text-lg font-semibold text-gray-900">Waitlistly</span>
                    </Link>
                    <Link
                        href="/"
                        className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </header>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-16">
                <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
                <p className="text-gray-500 mb-12">Last updated: January 2026</p>

                <div className="prose prose-gray max-w-none">
                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            By accessing or using Waitlistly ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            We reserve the right to update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
                        <p className="text-gray-600 leading-relaxed">
                            Waitlistly is an AI-powered landing page builder that helps users create waitlist pages to validate startup ideas. The Service includes:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2 mt-4">
                            <li>AI-generated landing page content based on user inputs</li>
                            <li>Customizable page templates and designs</li>
                            <li>Email collection and management (CRM)</li>
                            <li>Page hosting on Waitlistly subdomains or custom domains</li>
                            <li>Analytics and performance tracking</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            To use certain features of the Service, you must create an account. You agree to:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>Provide accurate and complete registration information</li>
                            <li>Maintain the security of your password and account</li>
                            <li>Notify us immediately of any unauthorized account access</li>
                            <li>Accept responsibility for all activities under your account</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. User Content</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            You retain ownership of all content you create using Waitlistly ("User Content"). By using the Service, you grant us a limited license to store, display, and process your content solely to provide the Service.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            You are solely responsible for ensuring your User Content does not violate any laws or third-party rights.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">You agree NOT to use the Service to:</p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>Create pages containing illegal, harmful, or offensive content</li>
                            <li>Engage in spam, phishing, or deceptive practices</li>
                            <li>Violate any applicable laws or regulations</li>
                            <li>Infringe on intellectual property rights of others</li>
                            <li>Collect data from others without proper consent</li>
                            <li>Attempt to disrupt or compromise the Service</li>
                            <li>Resell or redistribute the Service without authorization</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Payment Terms</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            Certain features of the Service require payment. By making a purchase, you agree to:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>Pay all applicable fees as displayed at the time of purchase</li>
                            <li>Provide accurate billing information</li>
                            <li>Authorize us to charge your selected payment method</li>
                        </ul>
                        <p className="text-gray-600 leading-relaxed mt-4">
                            All fees are non-refundable except as required by law or as expressly stated in our refund policy.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. AI-Generated Content</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            The Service uses artificial intelligence to generate landing page content. You acknowledge that:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>AI-generated content may require review and editing</li>
                            <li>We do not guarantee the accuracy or suitability of AI content</li>
                            <li>You are responsible for reviewing and approving all content before publishing</li>
                            <li>AI content should not be considered professional advice</li>
                        </ul>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Intellectual Property</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            The Service, including its design, features, and code, is owned by Waitlistly and protected by intellectual property laws. You may not copy, modify, or distribute any part of the Service without permission.
                        </p>
                        <p className="text-gray-600 leading-relaxed">
                            You retain ownership of your original User Content. AI-generated content created specifically for you may be used freely on your published pages.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            TO THE MAXIMUM EXTENT PERMITTED BY LAW, WAITLISTLY SHALL NOT BE LIABLE FOR:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>Any indirect, incidental, special, or consequential damages</li>
                            <li>Loss of profits, revenue, data, or business opportunities</li>
                            <li>Service interruptions or downtime</li>
                            <li>Actions of third parties using the Service</li>
                        </ul>
                        <p className="text-gray-600 leading-relaxed mt-4">
                            Our total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Termination</h2>
                        <p className="text-gray-600 leading-relaxed mb-4">
                            We may suspend or terminate your access to the Service at any time for:
                        </p>
                        <ul className="list-disc pl-6 text-gray-600 space-y-2">
                            <li>Violation of these Terms</li>
                            <li>Fraudulent or illegal activity</li>
                            <li>Non-payment of fees</li>
                            <li>Extended periods of inactivity</li>
                        </ul>
                        <p className="text-gray-600 leading-relaxed mt-4">
                            You may terminate your account at any time through your account settings. Upon termination, your published pages will be taken offline.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Governing Law</h2>
                        <p className="text-gray-600 leading-relaxed">
                            These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions. Any disputes arising from these Terms shall be resolved in the courts of Karnataka, India.
                        </p>
                    </section>

                    <section className="mb-10">
                        <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
                        <p className="text-gray-600 leading-relaxed">
                            If you have any questions about these Terms of Service, please contact us at:
                        </p>
                        <p className="text-gray-600 mt-4">
                            <strong>Email:</strong> hello@waitlistly.live<br />
                        </p>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <footer className="border-t border-gray-200 py-8">
                <div className="max-w-4xl mx-auto px-6 flex justify-center items-center gap-4">
                    <div className="flex items-center gap-6 text-sm text-gray-500">
                        <Link href="/terms" className="text-violet-600 font-medium">Terms of Service</Link>
                        <Link href="/privacy" className="hover:text-gray-900 transition-colors">Privacy Policy</Link>
                        <Link href="/contact" className="hover:text-gray-900 transition-colors">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
